import { useSelector } from 'react-redux';
import { Subtitle } from '../shared';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  IFarmResource,
  IMusselLineDetail,
} from '../../entities/farms.entities';
import {
  ICycleResource,
  IMusselSeeding,
} from '../../entities/growing.entities';
import { defaultDateFormat, displayValueUnit } from '../../lib/common.helpers';
import { formatNumber } from '../../entities/util-functions';
import {
  LineInventoriesLabel,
  SeedDensityRange,
  SeedInventoriesLabel,
  StageLabelSpan,
} from '../CommonViews';
import { selectAllFarms } from '../../store/extra/extra.selector';
import { selectAccount } from '../../store/auth/auth.selector';
import { getMusselHeaderLabel } from './LineHeadersModal';
import { getLineGrowingData } from '../../lib/farm.helpers';

interface Props {
  line: IMusselLineDetail;
  cycle: ICycleResource | null;
}

const MusselLineHeader = ({ line, cycle }: Props) => {
  const lang = useSelector(selectLang);
  const farm = useSelector(selectAllFarms).find(
    x => x.id === line.farm_id,
  ) as IFarmResource;
  const accountSetting = useSelector(selectAccount);
  const unitType = accountSetting?.display_unit;
  const musselLineHeaders = accountSetting?.mussel_line_headers ?? [];

  const musselSeedings =
    cycle?.crop_type === 'MUSSEL' ? (cycle.seedings as IMusselSeeding[]) : [];
  const longlineLength = cycle?.main_seed.is_growing
    ? line.growing_cycle?.current_line_length ?? 0
    : musselSeedings.reduce((p, c) => p + c.line_length, 0);
  const growingData = getLineGrowingData(line);

  const components = [
    {
      key: 'current_line_length',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('current_line_length', lang, line)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(
              longlineLength,
              unitType,
              line.line_type === 'LONGLINE' ? 'distance_m' : 'droppers',
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'backbone',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('backbone', lang, line, farm)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(line.backbone, unitType, 'distance_m', '-')}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'planned_date_seed',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('planned_date_seed', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {defaultDateFormat(cycle?.main_seed?.planned_date_seed, '-')}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'shell_size',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('shell_size', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(
              growingData.shell_size_avg,
              unitType,
              'size_mm',
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'planned_date_harvest',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('planned_date_harvest', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {defaultDateFormat(growingData.planned_date_harvest, '-')}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'spat_seed_type',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('spat_seed_type', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {cycle?.main_seed?.spat_storage?.seed_type ?? '-'}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'stage',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('stage', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {longlineLength > 0 &&
              (musselSeedings.length === 1 ? (
                <StageLabelSpan
                  stage={musselSeedings[0].stage}
                  farmId={line.farm_id}
                />
              ) : (
                musselSeedings.map((x, i) => (
                  <div key={i}>
                    {`${formatNumber(
                      (x.line_length / longlineLength) * 100,
                    )}% ${translate(lang, 'stage')} `}
                    <StageLabelSpan stage={x.stage} farmId={line.farm_id} />
                  </div>
                ))
              ))}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'beams_count',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('beams_count', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {line.basket_count}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'submersion',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('submersion', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(
              cycle?.main_seed?.submersion,
              unitType,
              'distance_m',
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'density',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('density', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {SeedDensityRange(growingData)}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'inventories',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('inventories', lang)}
          </Subtitle>
          <Subtitle size={5} color='black' align='left' fontWeight={500}>
            {cycle
              ? SeedInventoriesLabel(cycle.inventories_sum)
              : LineInventoriesLabel(line.pending_inventories ?? [])}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'predicted_amount',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('predicted_amount', lang)}
          </Subtitle>
          <Subtitle size={5} color='black' align='left' fontWeight={500}>
            {line.growing_cycle?.prediction?.harvest_amount
              ? formatNumber(
                  line.growing_cycle?.prediction?.harvest_amount / 1000,
                ) + ' T'
              : '-'}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'predicted_date',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('predicted_date', lang)}
          </Subtitle>
          <Subtitle size={5} color='black' align='left' fontWeight={500}>
            {defaultDateFormat(
              line.growing_cycle?.prediction?.harvest_date,
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'line_fill_percent',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('line_fill_percent', lang)}
          </Subtitle>
          <Subtitle size={5} color='black' align='left' fontWeight={500}>
            {displayValueUnit(line.fill_percent, unitType, '%', '-')}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'spacing',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('spacing', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(
              cycle?.main_seed.spacing,
              unitType,
              'size_mm',
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'drop',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getMusselHeaderLabel('drop', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(
              cycle?.main_seed.drop,
              unitType,
              'distance_m',
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
  ].filter(x => {
    if (farm.structure === 'RAFT') {
      if (x.key === 'available_length') return false;
    } else {
      if (x.key === 'beams_count' || x.key === 'spacing') return false;
    }
    if (!line.growing_cycle) {
      if (
        [
          'planned_date_seed',
          'shell_size',
          'planned_date_harvest',
          'spat_seed_type',
          'stage',
          'submersion',
          'density',
          'predicted_amount',
          'predicted_date',
          'spacing',
          'drop',
        ].includes(x.key)
      )
        return false;
    }
    return musselLineHeaders.includes(x.key);
  });

  return (
    <div className='line-headers'>
      {components.map(h => (
        <div key={h.key}>{h.component}</div>
      ))}
    </div>
  );
};

export default MusselLineHeader;
